import { useRef, useCallback, useState } from 'react';
import { Button, Typography, Badge, Modal } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useSimulation } from '../../context/SimulationContext';
import { useArtifactTracking } from '../../hooks/useArtifactTracking';
import ScenarioPanel from '../ScenarioPanel/ScenarioPanel';
import ArtifactWorkspace from '../ArtifactWorkspace/ArtifactWorkspace';
import FeedbackPanel from '../FeedbackPanel/FeedbackPanel';
import ActionInput from '../ActionInput/ActionInput';
import ReflectionPanel from '../ReflectionPanel/ReflectionPanel';
import TimelinePanel from '../TimelinePanel/TimelinePanel';
import MutationOverlay from '../MutationOverlay/MutationOverlay';
import TimeWarningBanner from '../TimeWarningBanner/TimeWarningBanner';
import InstructionsContent from '../InstructionsScreen/InstructionsContent';
import TurnFeedbackBanner from '../TurnFeedbackBanner/TurnFeedbackBanner';
import './SimulationShell.scss';

function SimulationShell() {
  const {
    turnIndex,
    phase,
    isSubmitting,
    isConnected,
    isReconnecting,
    sessionStatus,
    actionText,
    submitTurn,
    reflectionText,
  } = useSimulation();

  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const trackingRef = useRef<ReturnType<typeof useArtifactTracking> | null>(
    null,
  );

  const handleTrackingReady = useCallback(
    (tracking: ReturnType<typeof useArtifactTracking>) => {
      trackingRef.current = tracking;
    },
    [],
  );

  const canSubmit =
    actionText.trim().length > 0 &&
    !isSubmitting &&
    sessionStatus === 'active' &&
    isConnected;

  const handleSubmit = () => {
    if (!trackingRef.current) return;
    const { artifactsBefore, artifactsAfter, artifactReadCount, artifactWriteCount } =
      trackingRef.current.getSnapshots();

    submitTurn(
      actionText,
      reflectionText,
      artifactsBefore,
      artifactsAfter,
      artifactReadCount,
      artifactWriteCount,
    );

    trackingRef.current.resetTracking();
  };

  return (
    <div className="simulation-shell">
      <MutationOverlay />
      <TimeWarningBanner />

      <Modal
        title="Simulation Instructions"
        open={instructionsOpen}
        onCancel={() => setInstructionsOpen(false)}
        footer={
          <Button type="primary" onClick={() => setInstructionsOpen(false)}>
            Close
          </Button>
        }
        width={720}
      >
        <InstructionsContent />
      </Modal>

      {/* Reconnecting overlay */}
      {isReconnecting && (
        <div className="reconnecting-overlay">
          <Typography.Text>Reconnecting...</Typography.Text>
        </div>
      )}

      {/* Header */}
      <div className="simulation-header">
        <Typography.Text strong>
          Turn {turnIndex + 1} &middot; Phase {phase}
        </Typography.Text>
        <div className="simulation-header-right">
          <Typography.Link onClick={() => setInstructionsOpen(true)}>
            <QuestionCircleOutlined /> View Instructions
          </Typography.Link>
          <Badge
            status={isConnected ? 'success' : 'error'}
            text={isConnected ? 'Connected' : 'Disconnected'}
          />
        </div>
      </div>

      {/* Main Layout */}
      <div className="simulation-content">
        <div className="simulation-left">
          <ScenarioPanel />
          <ArtifactWorkspace onTrackingReady={handleTrackingReady} />
        </div>
        <div className="simulation-right">
          <FeedbackPanel />
          <TimelinePanel />
        </div>
      </div>

      {/* Input Area */}
      <div className="simulation-input-area">
        <TurnFeedbackBanner />
        <ActionInput />
        <ReflectionPanel />
      </div>

      {/* Footer */}
      <div className="simulation-footer">
        <Button
          type="primary"
          size="large"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!canSubmit}
        >
          Submit Turn
        </Button>
      </div>
    </div>
  );
}

export default SimulationShell;
